import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { FLAVORS, VIBE_TAGS, type Flavor } from '@web/shared';
import {
  Button,
  Field,
  Input,
  Sticker,
  Avatar,
  Badge,
  EmojiPicker,
  PulseLoader,
} from '@/components/ui';
import { flavorVar } from '@/components/ui';
import { ApiRequestError } from '@/lib/api';
import { useOnboard, useHandleAvailable } from '@/features/profile/api';
import { applyFlavorTheme } from '@/features/profile/flavor-theme';
import { getDraft, clearDraft, consentComplete } from '@/features/onboarding/store';

export function OnboardingProfilePage() {
  const navigate = useNavigate();
  const draft = getDraft();
  const [handle, setHandle] = useState('');
  const [emoji, setEmoji] = useState('😎');
  const [flavor, setFlavor] = useState<Flavor>('lychee');
  const [bio, setBio] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Live-preview the flavor across the whole app as they try colours.
  useEffect(() => {
    applyFlavorTheme(flavor);
  }, [flavor]);

  const onboard = useOnboard();
  const clean = handle
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, '')
    .slice(0, 20);
  // Checking is manual (a button, or a submit-time check) rather than firing
  // on every keystroke — `check.data` is scoped to `clean` by query key, so
  // editing the handle after a check drops the old ✓/✗ automatically.
  const check = useHandleAvailable(clean);
  const handleState = useMemo(() => {
    if (clean.length < 3) return 'short';
    if (check.isFetching) return 'checking';
    if (check.data?.available === false) return 'taken';
    if (check.data?.available === true) return 'ok';
    return 'unchecked';
  }, [clean, check.isFetching, check.data]);

  if (!consentComplete(draft)) {
    void navigate({ to: '/onboarding/terms' });
    return null;
  }

  const toggleTag = (t: string) =>
    setTags((cur) =>
      cur.includes(t) ? cur.filter((x) => x !== t) : cur.length < 5 ? [...cur, t] : cur,
    );

  const submit = async () => {
    setErr(null);
    setSubmitting(true);
    try {
      // Didn't tap "Check"? Check now, right before submitting — never let an
      // unverified handle through.
      if (handleState !== 'ok') {
        if (clean.length < 3) {
          setErr('Pick a handle (3–20 chars, a–z 0–9 _).');
          return;
        }
        const res = await check.refetch();
        if (res.data?.available !== true) {
          setErr(
            res.data?.available === false
              ? 'That handle is taken — try another.'
              : 'Could not check that handle — try again.',
          );
          return;
        }
      }
      await onboard.mutateAsync({
        acceptedAge18: true,
        acceptedTerms: true,
        acceptedLocation: true,
        termsVersion: draft.termsVersion,
        handle: clean,
        vibeEmoji: emoji,
        flavor,
        bio: bio.trim() || undefined,
        tags: tags as never,
      });
      clearDraft();
      void navigate({ to: '/app' });
    } catch (e) {
      setErr(e instanceof ApiRequestError ? e.message : 'Could not save. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <Sticker flavor="melon" rotate={-3}>
          setup · 4 / 4
        </Sticker>
        <h1 className="mt-4 text-3xl font-semibold">Make your handle</h1>
        <p className="mt-2 text-sm text-ink-soft">
          This is what people see if a Whisper turns into something. To nearby strangers you’re just{' '}
          <span className="font-semibold">
            Anonymous {flavor.charAt(0).toUpperCase() + flavor.slice(1)}
          </span>
          .
        </p>
      </div>

      <div className="flex items-center gap-3">
        <Avatar flavor={flavor} glyph={emoji} size="lg" />
        <p className="text-xs text-ink-faint">pick your face below</p>
      </div>
      <EmojiPicker value={emoji} onChange={setEmoji} />

      <Field
        label="Handle"
        error={
          handleState === 'taken'
            ? 'Taken — try another'
            : err && handleState !== 'ok'
              ? err
              : undefined
        }
        hint={
          handleState === 'ok'
            ? '✓ available'
            : handleState === 'checking'
              ? 'checking…'
              : 'lowercase letters, numbers, underscores'
        }
      >
        <div className="flex gap-2">
          <Input
            value={handle}
            onChange={(e) => setHandle(e.target.value)}
            placeholder="lychee_kid"
            autoCapitalize="none"
            autoCorrect="off"
            disabled={check.isFetching}
            className="flex-1"
          />
          <Button
            type="button"
            variant="outline"
            disabled={clean.length < 3 || check.isFetching}
            onClick={() => check.refetch()}
          >
            {check.isFetching ? (
              <PulseLoader size="sm" />
            ) : handleState === 'ok' ? (
              '✓ available'
            ) : (
              'Check'
            )}
          </Button>
        </div>
      </Field>

      <div>
        <p className="mb-1.5 text-sm font-medium">Your color</p>
        <div className="flex gap-2">
          {FLAVORS.map((f) => (
            <button
              key={f}
              onClick={() => setFlavor(f)}
              aria-label={f}
              className={`size-9 rounded-blob border-2 ${flavor === f ? 'border-ink' : 'border-transparent'}`}
              style={{ backgroundColor: flavorVar(f) }}
            />
          ))}
        </div>
      </div>

      <Field label="One-line bio (optional)" hint={`${bio.length}/140`}>
        <Input
          value={bio}
          maxLength={140}
          onChange={(e) => setBio(e.target.value)}
          placeholder="here for the music, staying for the people"
        />
      </Field>

      <div>
        <p className="mb-1.5 text-sm font-medium">
          Your vibe <span className="font-normal text-ink-faint">— pick up to 5</span>
        </p>
        <div className="flex flex-wrap gap-1.5">
          {VIBE_TAGS.map((t) => (
            <button key={t} onClick={() => toggleTag(t)}>
              <Badge tone={tags.includes(t) ? 'accent' : 'neutral'}>{t}</Badge>
            </button>
          ))}
        </div>
      </div>

      {err && handleState === 'ok' && <p className="text-sm text-critical">{err}</p>}

      <Button variant="candy" size="lg" onClick={submit} disabled={submitting}>
        {submitting ? <PulseLoader flavor="melon" size="sm" /> : 'Enter Nearmirer'}
      </Button>
    </div>
  );
}
